import { MINUTES_PER_DAY } from '../model/constants';

/** A half-open interval of wall minutes `[startMin, endMin)` measured from 00:00 of a personal date. */
export interface Span {
  readonly startMin: number;
  readonly endMin: number;
}

/** A span that belongs to an identified record. */
export interface IdSpan extends Span {
  readonly id: string;
}

/**
 * Time settings shared by all geometry operations (2026-10b §3.4): the step is the snapping
 * grid of pointer and keyboard edges, the minimum duration the shortest block a pointer
 * gesture or keyboard nudge may produce. Only `snap*` calls use the step; only length floors
 * use the minimum duration — it is not a data invariant.
 */
export interface GeometryOptions {
  /** Time step in minutes (5, 15, 30 or 60). */
  readonly step: number;
  /** Minimum duration in minutes of a pointer- or keyboard-made block (a multiple of the step). */
  readonly minDuration: number;
  /** Personal-day start in wall minutes (multiple of 60, 0 to 1439). */
  readonly dayStartMin: number;
}

export interface CreateOptions extends GeometryOptions {
  readonly defaultDurationMin: number;
}

/** Nearest multiple of `unit`; exact halves round up. */
export function snap(min: number, unit: number): number {
  return Math.round(min / unit) * unit;
}

export function snapDown(min: number, unit: number): number {
  return Math.floor(min / unit) * unit;
}

export function snapUp(min: number, unit: number): number {
  return Math.ceil(min / unit) * unit;
}

/**
 * The next grid line of `unit` from `min` in `direction` (keyboard nudges, 2026-10b §3.5):
 * a value already on a line moves one whole unit; a value between lines moves to the nearest
 * line in that direction.
 */
export function nextGridLine(min: number, unit: number, direction: 1 | -1): number {
  return direction > 0 ? Math.floor(min / unit) * unit + unit : Math.ceil(min / unit) * unit - unit;
}

/** A personal day: `[dayStartMin, dayStartMin + 1440]`. */
export function dayBounds(dayStartMin: number): Span {
  return { startMin: dayStartMin, endMin: dayStartMin + MINUTES_PER_DAY };
}

export function durationOf(span: Span): number {
  return span.endMin - span.startMin;
}

export function clamp(value: number, low: number, high: number): number {
  return Math.min(Math.max(value, low), high);
}

export function spansOverlap(a: Span, b: Span): boolean {
  return a.startMin < b.endMin && b.startMin < a.endMin;
}

/**
 * Free gaps inside `bounds` once `others` are removed, ordered by start. Zero-length gaps are
 * dropped. `others` may overlap each other and may extend beyond `bounds`.
 */
export function freeGaps(others: readonly Span[], bounds: Span): Span[] {
  const sorted = others
    .filter((other) => other.endMin > bounds.startMin && other.startMin < bounds.endMin)
    .sort((a, b) => a.startMin - b.startMin || a.endMin - b.endMin);
  const gaps: Span[] = [];
  let cursor = bounds.startMin;
  for (const other of sorted) {
    if (other.startMin > cursor) gaps.push({ startMin: cursor, endMin: other.startMin });
    cursor = Math.max(cursor, other.endMin);
  }
  if (cursor < bounds.endMin) gaps.push({ startMin: cursor, endMin: bounds.endMin });
  return gaps;
}

/** The free gap containing wall minute `min` (start inclusive, end exclusive), or null. */
export function gapContaining(min: number, others: readonly Span[], bounds: Span): Span | null {
  return freeGaps(others, bounds).find((gap) => gap.startMin <= min && min < gap.endMin) ?? null;
}
